
// @ts-check

/**
 * @typedef {import("./coreTypes").ResultAtom} ResultAtom
 * @typedef {import("./coreTypes").JSResultAtom} JSResultAtom
 * @typedef {import("./coreTypes").QuerySpec} QuerySpec
 */

import React, { useEffect, useState } from "react";
import WebCampaignInfoScreen from "./WebCampaignInfoScreen";
import CampaignList, { getQueryName } from "./CampaignList";
import DonorCardWebLayout from "./DonorCardWebLayout";

import { resultAtomsToParsedDonors } from "./paginator";
import { determineLeftOrRightMarginToCard } from "./processQuerySpec";
import { getResultsFor } from "./GetQueryResultsFunctions";

import ResultsLoadingManagerWebInstance from "./ResultsLoadingManagerWebInstance";


// @ts-ignore
import removeSearchIcon from "../images/remove_search--white.svg";

import "../styles/WebResultsCampaignContent.css";

import "../styles/WebLoadingText.css";


// @ts-ignore
import searchIcon from "../images/search_icon--white.svg";
import NamedBuildingOnePageLayout from "./NamedBuildingOnePageLayout";
import { pushDonorListIsPopulated } from "./AnalyticsFunctions";

function rtrim(str, chr) {
    if (str.indexOf(',') > -1) {
        str = str.split(',').reverse().join(' ');
    }
    var rgxtrim = (!chr) ? new RegExp('\\s+$') : new RegExp(chr+'+$');
    return str.replace(rgxtrim, '');
  }
/**
 * @param {{ campQueryName: string, }} props 
 * @returns {JSX.Element | null}
 */
 function WebResultsCampaignContent(props) {

    /**
     * @type {[JSResultAtom[], React.Dispatch<React.SetStateAction<JSResultAtom[]>>]}
     */
    // @ts-ignore
    const [donors, setDonors] = useState([]);
    const [lastFilterStringUsed, setLastFilterStringUsed] = useState('');
    const [filterString, setFilterString] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);

    const [donorMiddleDynamicStyle, setDonorMiddleDynamicStyle] = 
        useState({ 
            ...donorMiddleStyle,
            marginLeft: 0,
            marginRight: 0,
         });

    const comb = ResultsLoadingManagerWebInstance.generateCombinedMethodsFromSetter(setDonors);
    const generateLoadingIdAndSetAsActive = comb.generateLoadingIdAndSetAsActive;
    const setDonorsIfCurrentLoadingId = comb.setResultsIfCurrentLoadingId;   


    const shouldDisplayRemoveFilterIcon = 
        (lastFilterStringUsed !== '') && (lastFilterStringUsed === filterString);



    /**
     * @param {QuerySpec} querySpec 
     * @param {function(ResultAtom[]):JSResultAtom[]} resToDonors 
     * @param {boolean} useAnalytics
     * @return {void}
     */
    const setDonorsIncrementally = (querySpec, resToDonors, useAnalytics) => {

        const id = generateLoadingIdAndSetAsActive();
        setIsLoading(true);
        setDonors([]);

        const errorHandler = _ => { setIsLoading(false); setIsError(true); };
        const resultsObj = getResultsFor(querySpec, 50);

        /** @type {boolean} */
        let analyticsSent = false;

        /**
         * @type {function(ResultAtom[]):void}
         */
        let nextHandler;
        nextHandler = (res) => {
            const donors = resToDonors(res);
            // there are donors to add.
            if (ResultsLoadingManagerWebInstance.getCurrentLoadingId() === id
                && donors.length > 0) 
            {
                if (useAnalytics && !analyticsSent) {
                    analyticsSent = true;
                    pushDonorListIsPopulated(true);
                }
                setDonors(d => d.concat(donors));
                resultsObj.getNext().then(nextHandler).catch(errorHandler);
            }
            // end of results from server.
            else if (ResultsLoadingManagerWebInstance.getCurrentLoadingId() === id
                && res.length === 0) {
                if (useAnalytics) {
                    pushDonorListIsPopulated(false);
                }
                setIsLoading(false);
            }
            // user switched to something else.
            else if (ResultsLoadingManagerWebInstance.getCurrentLoadingId() !== id) {
                // Don't set loading to false here, because something else is loading.
            }
            // everything in this batch was filtered out.
            else {
                resultsObj.getNext().then(nextHandler).catch(errorHandler);
            }
        };
        resultsObj.initial.then(res => {

            const donors = resToDonors(res);
            setDonorsIfCurrentLoadingId(id, donors);
            setIsError(false);
            
            if (donors.length > 0) {
                resultsObj.getNext().then(nextHandler).catch(errorHandler);
            }
            else if (res.length === 0) {
                if (useAnalytics) {
                    pushDonorListIsPopulated(false);
                }
                setIsLoading(false);
            } 
            else {
                resultsObj.getNext().then(nextHandler).catch(errorHandler);
            }
        }).catch(errorHandler);
    };



    useEffect(() => {
        if (props.campQueryName !== 'Named Buildings') {

            /** @type {QuerySpec} */
            const querySpec = [['campaign_name', props.campQueryName]];

            setDonorsIncrementally(querySpec, resultAtomsToParsedDonors, false);
        }
    // eslint-disable-next-line
    }, [props.campQueryName]);


    /**
     * @param {string} filter 
     * @returns {void}
     */
    const performFiltering = (filter) => {
        if (props.campQueryName !== 'Named Buildings') {

            setLastFilterStringUsed(filter);

             /** @type {QuerySpec} */
             const querySpec = [['campaign_name', props.campQueryName]]

             /**
              * @param {ResultAtom[]} res 
              * @returns {JSResultAtom[]}
              */
             const resultAtomsToFilteredDonors = res =>
                resultAtomsToParsedDonors(res).filter(d => { 
                        const fullName = d[1].firstName + ' ' + d[1].lastName; 
                        return fullName
                            .toLocaleLowerCase()
                            .includes(filter.toLocaleLowerCase());
                    });

             setDonorsIncrementally(
                querySpec, 
                r =>
                    (filter === '') 
                        ? resultAtomsToParsedDonors(r) 
                        : resultAtomsToFilteredDonors(r),
                true);
        }
    };


    const updateMiddleDynamicStyle = () => {
        let middleDynamicMargin = determineLeftOrRightMarginToCard(window.screen.width);
        setDonorMiddleDynamicStyle({ 
            ...donorMiddleStyle, 
            marginLeft: middleDynamicMargin,
            marginRight: middleDynamicMargin, });
    };

    useEffect(() => { 
        updateMiddleDynamicStyle();
        window.addEventListener("resize", updateMiddleDynamicStyle);
        return () => {
            window.removeEventListener("resize", updateMiddleDynamicStyle);
        };
    }, []);


    const clearOutFilter = () => {
        setFilterString("");
        setLastFilterStringUsed("");
        performFiltering("");
    };


    // Treat Named Buildings specially.
    if (props.campQueryName === 'Named Buildings') {
        return (<NamedBuildingOnePageLayout/>);
    }
    else {

        const campObj = 
            CampaignList.find(f => getQueryName(f) === props.campQueryName);

        // Can't find the image for the campaign
        if (campObj === undefined) return null;

        const title = campObj.title;
        const image = campObj.image.full.url;
        const info = campObj.info;

        // campaign card then donor cards with donor 
        return (
            <div style={containerStyle}>                
                <WebCampaignInfoScreen 
                    title={title} 
                    info={info} 
                    image={image} 
                    mirrored={false}
                    narrow={false} />
                {/** @ts-ignore */}
                <div style={donorMiddleDynamicStyle}>
                    <p style={donorsHeaderTextStyle}>Donors to this campaign</p>
                    {/** @ts-ignore */}
                    <div style={filterDonorNamesContainerStyle}>
                        <input 
                            type="text" 
                            id="web-res-campaign-filter-donor-names" 
                            placeholder="Filter donor names" 
                            aria-label="Filter donor names - names are filtered as you type"
                            style={filterDonorNamesInputStyle} 
                            value={filterString}
                            onChange={e => setFilterString(e.target.value)} 
                            onKeyUp={e => {
                              if (e.key === "Enter") { performFiltering(rtrim(filterString)); }  
                            }}/>
                        {/** @ts-ignore */}
                        <img src={searchIcon} className="web-res-campaign__filter-donor-search__icon" onClick={() => performFiltering(rtrim(filterString))} alt="Filter Donors"/>
                        { shouldDisplayRemoveFilterIcon && 
                            <img 
                                className="web-res-campaign__filter-donor-search__remove-search__icon"
                                src={removeSearchIcon} 
                                onClick={clearOutFilter} 
                                alt="Clear Filter" />}
                    </div>
                </div>
                { !isError && !isLoading && donors.length === 0 && <p style={errorStyle}>There are no results.</p>}
                { !isError && <DonorCardWebLayout donors={donors} singleColumn={false} /> }
                { !isLoading && isError && <p style={errorStyle}>There was an error fetching the list of donors.</p> }
                <p style={{ paddingTop: 50 }}></p>
                { (isLoading) 
                    ? <div className="web-loading-text__container">
                        <p className="web-loading-text__loading">Loading</p>
                        <p className="web-loading-text__dot-1"> . </p>
                        <p className="web-loading-text__dot-2"> . </p>
                        <p className="web-loading-text__dot-3"> . </p>
                      </div> 
                    : null }
            </div>);
    }   
}

const errorStyle = {
    color: 'white',
    fontSize: 16,
    fontFamily: 'Arial',
};

const containerStyle = {
    
};

const donorMiddleStyle = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 80,
    marginBottom: 20,
};

const donorsHeaderTextStyle = {
    color: 'white',
    fontFamily: 'Arial',
    fontSize: 32,
    fontWeight: 'bold',
};

const filterDonorNamesContainerStyle = {
    position: 'relative',
    borderBottomWidth: 2,
    borderBottomColor: 'white',
    borderBottomStyle: 'solid',
};

const filterDonorNamesInputStyle = {
    width: 240,
    height: 40,
    fontFamily: 'Arial',
    fontSize: '11pt',
    color: 'white',
    background: 'transparent',
    paddingLeft: 35,
};



export default WebResultsCampaignContent;